// import downloadPdf from 'dom-to-pdf';
import { ReadonlyURLSearchParams } from 'next/navigation';

import { appSettings } from '@/settings/app-settings';
import {
  LISTITEM_TYPE_BOLDER_ASSET_BOOKABLE,
  LISTITEM_TYPE_BOLDER_ASSET_DONATION,
  LISTITEM_TYPE_BOLDER_ASSET_EVENT,
  LISTITEM_TYPE_BOLDER_ASSET_LOTTERY,
  LISTITEM_TYPE_BOLDER_ASSET_PHYSICAL,
  LISTITEM_TYPE_BOLDER_ASSET_THIRD_PARTY,
} from '@/ux-legacy/experience/products/products-contstants';
import { IMG_PLACEHOLDER } from '@/ux-legacy/uikit/utils/img-placeholder';

import { downloadPdf } from './dom-to-pdf';

export function classNameX(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export function toLowerNoSpace(text = '') {
  return text.replace(' ', '-').toLowerCase();
}

export function isServer() {
  return typeof window == 'undefined';
}

export function isVideoPlaying(video: HTMLVideoElement) {
  return !!(video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2);
}

export function appendImageRepoDomainIfNotExistInUrl(url = '') {
  // Falsy in, empty out. The default parameter only covers `undefined`, and a
  // record whose image field is `null` reached `.startsWith` and threw — which
  // matters now that callers pass an unguarded `x?.displayImage` straight in
  // rather than concatenating behind a truthiness check.
  if (!url) {
    return '';
  }

  const mediaUrl = appSettings.api.S3_MEDIA || '';
  const compressedUrl = appSettings.api.S3_MEDIA_COMPRESSED || '';

  if (url.startsWith(mediaUrl)) {
    return url.replace(mediaUrl, compressedUrl);
  }

  if (
    !url?.includes(compressedUrl) &&
    !url?.includes('data:image') &&
    !url?.includes('blob:') &&
    !url?.includes('https')
  ) {
    return compressedUrl + url;
  }

  return url;
}

/**
 * Whether `url` lives on our own image CDN (the S3_MEDIA_COMPRESSED host, the
 * only remote host added to next.config `images.remotePatterns`) and is
 * therefore safe to send through the next/image optimizer.
 *
 * Arbitrary/external URLs — e.g. absolute https link-preview OG images, which
 * appendImageRepoDomainIfNotExistInUrl deliberately leaves untouched — must be
 * rendered `unoptimized`, otherwise next/image throws an un-catchable "invalid
 * src" error for an unconfigured remote host (a plain <img> rendered them fine).
 */
export function isOptimizableImageUrl(url = ''): boolean {
  const compressedUrl = appSettings.api.S3_MEDIA_COMPRESSED || '';
  return !!compressedUrl && !!url && url.startsWith(compressedUrl);
}

/**
 * @deprecated Direct use is retired in favour of `waitForImageReady` from `@/core/images/`,
 * which prefers the image manifest `status`. This legacy `new Image()` CDN-propagation poll now
 * survives only as `waitForImageReady`'s internal fallback for images that never get a manifest
 * (non-Lambda sources such as AI design results, or pre-deploy legacy assets).
 */
export function getRefreshedImage(url: string): Promise<string> {
  const pollingInterval = 1000;
  let count = 0;

  return new Promise((resolve) => {
    const loadImage = () => {
      const image = new Image();
      image.src = url;

      image.onload = () => {
        resolve(url);
      };

      image.onerror = () => {
        if (url && count <= 10) {
          poll();
          count++;
        }

        if (count > 10) {
          resolve(IMG_PLACEHOLDER);
        }
      };
    };

    const poll = () => {
      setTimeout(() => {
        loadImage();
      }, pollingInterval);
    };

    loadImage();
  });
}

export function dontCapitalize(text: string) {
  return text;
}

export function hexToRGB(hex: string, alpha: number) {
  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  if (alpha) {
    return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
  } else {
    return 'rgb(' + r + ', ' + g + ', ' + b + ')';
  }
}

export function getVideoThumbLineUrl(url: string) {
  if (typeof url === 'string') {
    return url?.replace('m3u8', '0000000.jpg');
  } else {
    return '';
  }
}

export function getServerHost(host: string) {
  if (host.includes('localhost')) {
    return 'http://' + host;
  }

  return 'https://' + host;
}

export function formatCount(count: number) {
  if (count < 1000) {
    return count.toString();
  } else if (count < 1000000) {
    return (count / 1000).toFixed(1) + 'k';
  } else if (count < 1000000000) {
    return (count / 1000000).toFixed(1) + 'm';
  } else {
    return (count / 1000000000).toFixed(1) + 'b';
  }
}

export function isMobile() {
  const regex = /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  return regex.test(navigator.userAgent);
}

export function getPortalHeightFactor(width: number, force = false) {
  if (width < 360 && !force) {
    return [340, 340 / 16];
  }
  return [width - 20, (width - 20 * 4) / 16];
}

export const isValidEmail = (email: string) => {
  // Regular expression for basic email format validation
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9-]+$/;
  return emailRegex.test(email);
};

export function maskEmail(email: string): string {
  const atIndex = email.indexOf('@');
  if (atIndex < 0) return email;
  const username = email.slice(0, atIndex);
  const domain = email.slice(atIndex + 1);
  const masked = username.substring(0, 2) + '*'.repeat(Math.max(3, username.length - 2));
  return masked + '@' + domain;
}

export function bufferToBase64(buffer: any) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function getProfilePath(mode: string) {
  return `//${mode === 'production' ? '' : `${mode}.`}bolder.me/p/`;
}

export const createQueryString = (searchParams: ReadonlyURLSearchParams, name: string, value: string) => {
  const params = new URLSearchParams(searchParams.toString());
  params.set(name, value);
  return params.toString();
};

export const isMacOS = () => {
  return /Mac/.test(navigator.userAgent);
};
export const isWindows = () => {
  return /Win/.test(navigator.userAgent);
};

export const dataURLtoBlob = (dataUrl: string) => {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)![1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

export function generateRandomId(length: number) {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export const filesize = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export function validateUrl(value = '') {
  return /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(
    value,
  );
}

export function replaceLocalhost(value = '') {
  return value
    .replace('http://localhost:3000', `https://${appSettings.api.DEV_FALLBACK_HOST}/`)
    .replace('https://localhost:3000', `https://${appSettings.api.DEV_FALLBACK_HOST}/`);
}

export function defaultHost(value = '') {
  return value.replace('localhost:3000', appSettings.api.DEV_FALLBACK_HOST);
}

export function convertTo12HourFormat(time24: string) {
  const [hours, minutes] = time24.split(':').map(Number);
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const updatedHours = hours % 12 || 12;
  const minutesWithPadding = minutes.toString().padStart(2, '0');
  return `${updatedHours}:${minutesWithPadding} ${suffix}`;
}

export function convertTo24HourFormat(time12: string) {
  const [time, period] = time12.split(' ');

  const [hours, minutes] = time.split(':').map(Number);
  let updatedHours = hours;
  if (period === 'PM' && hours !== 12) {
    updatedHours += 12;
  } else if (period === 'AM' && hours === 12) {
    updatedHours = 0;
  }
  const hourWithPadding = updatedHours.toString().padStart(2, '0');
  const minutesWithPadding = minutes.toString().padStart(2, '0');

  return `${hourWithPadding}:${minutesWithPadding}`;
}

export function getTextParts(text: string) {
  const textParts: string[] = [];
  let part = '';
  let tagOpened = false;

  //console.log(' getTextParts START -------------------- ');

  // if (/Magnus/.test(text)) {
  //   console.log(' --- ');
  // }

  const pushPart = () => {
    if (part !== '') {
      // console.log(' getTextParts PUSH ' + part);
      textParts.push(part);
    }
  };

  for (let i = 0; i < text.length; i++) {
    const character = text[i];
    // console.log(' getTextParts CHAR ' + character);
    if (character + text[i + 1] === '<a') {
      pushPart();
      tagOpened = true;
      part = '<a';
      i++;
    } else if (character + text[i + 1] === 'a>') {
      tagOpened = false;
      part += 'a>';
      pushPart();
      part = '';
      i++;
    } else if (
      character + text[i + 1] + text[i + 2] + text[i + 3] === '<br>' ||
      character + text[i + 1] + text[i + 2] + text[i + 3] === '<BR>'
    ) {
      pushPart();
      part = '<br>';
      pushPart();
      part = '';
      i += 3;
    } else if (character !== ' ') {
      part += character;
    } else if (character === ' ') {
      if (!tagOpened) {
        pushPart();
        part = '';
      } else {
        part += character;
      }
    }
  }
  pushPart();

  return textParts;
}

export function formatNumber(num: number) {
  if (num === Math.floor(num)) {
    return Math.floor(num);
  } else {
    return num;
  }
}

export function preventAlphaInput(e: any) {
  // Allow: backspace, delete, dot (.), and arrow keys
  if (
    [46, 8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 || // dot (190), numpad dot (110)
    (e.keyCode >= 35 && e.keyCode <= 40) || // home, end, arrow keys
    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    (e.ctrlKey === true && [65, 67, 86, 88].indexOf(e.keyCode) !== -1)
  ) {
    return; // Let these key actions pass through
  }

  // Ensure it's a digit (0-9)
  if ((e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
    e.preventDefault(); // Block non-digit characters
  }
}

export function exportDomToPdf(domElement: any, fileName?: string) {
  const options = {
    filename: fileName ? fileName + '.pdf' : 'download.pdf',
    excludeTagNames: ['input', 'select'],
    excludeClassNames: ['exclude-item-from-Pdf'],
  };
  downloadPdf(domElement, options, function (pdf: any) {
    console.log('PDF Exported');
  });
}

export function geProductPagetLink(type: number, assetId: string) {
  let url;

  if (type == LISTITEM_TYPE_BOLDER_ASSET_BOOKABLE) {
    url = `/booking?productId=${assetId}`;
  } else if (type == LISTITEM_TYPE_BOLDER_ASSET_EVENT) {
    url = `/event?productId=${assetId}`;
  } else if (type == LISTITEM_TYPE_BOLDER_ASSET_THIRD_PARTY) {
    url = `/digital?productId=${assetId}`;
  } else if (type == LISTITEM_TYPE_BOLDER_ASSET_DONATION) {
    url = `/donation?productId=${assetId}`;
  } else if (type == LISTITEM_TYPE_BOLDER_ASSET_PHYSICAL) {
    url = `/ecommerce?productId=${assetId}`;
  } else if (type == LISTITEM_TYPE_BOLDER_ASSET_LOTTERY) {
    url = `/lottery?productId=${assetId}`;
  } else {
    url = `/product?type=${type}&productId=${assetId}`;
  }
  return url;
}

/**
 * Extract a string ID from a value that may be either a plain string ID
 * or a populated object with `_id` / `id`.
 *
 * Common in MongoDB/Mongoose responses where a field can be a raw ObjectId
 * string or a populated sub-document.
 */
export function resolveId(
  value: string | { _id?: string; id?: string } | null | undefined,
): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  return value._id || value.id;
}

export function convertToPlain(html: string) {
  const tempDivElement = document.createElement('div');
  tempDivElement.innerHTML = html;
  return tempDivElement.textContent || tempDivElement.innerText || '';
}
